import { useEffect, useRef, useState } from 'react';

import { CoreTelemetryController } from '../../logger/CoreTelemetryController';
import { useUITranslation } from '../../services/uiTranslations/reactBindings/hooks';
import { useConfigSlice } from '../../store/slices/config/selectors';

/**
 * Hook that handles curtain disappearing when game is loaded
 * @returns boolean value, that represents if the curtain disappearing animation has finished
 */
export const useCurtain = (progress: number): boolean => {
  const session = useConfigSlice.session();
  const curtain = useRef<HTMLDivElement>(document.querySelector<HTMLDivElement>('.curtain'));
  const showJurMsg = useConfigSlice.casinoConfig()?.showAlderneyMsg;
  const gamblingCommissionTitle = useUITranslation('GAMBLING_COMMISSION_TITLE');
  const gamblingCommissionText = useUITranslation('AGCC_REGULATORY_MESSAGE');
  const [curtainVisible, setCurtainVisible] = useState<boolean>(true);

  useEffect(() => {
    const curtainElement = curtain.current;
    if (curtainElement) {
      const progressBar = curtainElement.querySelector<HTMLProgressElement>('progress');

      // get access to curtain footer
      const curtainFooter = curtainElement.querySelector<HTMLDivElement>('.curtain-footer');
      if (curtainFooter && showJurMsg && gamblingCommissionTitle && gamblingCommissionTitle) {
        // curtain footer header and message accordingly
        const titleNode = curtainFooter.childNodes[0];
        const textNode = curtainFooter.childNodes[1];
        if (titleNode && textNode) {
          titleNode.textContent = gamblingCommissionTitle;
          textNode.textContent = gamblingCommissionText;
        }
        curtainFooter.classList.add('visible');
      } else {
        curtainFooter?.classList.remove('visible');
      }

      if (!progressBar) return;
      progressBar.value = progress;
      if (progress === 100 || session === null) {
        CoreTelemetryController.logInitialTelemetry();
        curtainElement.classList.add('curtain_lifted');
        const animationEndHandler = (): void => {
          if (curtainElement) {
            curtainElement.classList.add('curtain_dead');
            curtainElement.classList.remove('curtain_lifted');
            setCurtainVisible(false);
          }
        };
        curtainElement.addEventListener('animationend', animationEndHandler);
        return (): void => {
          curtainElement.removeEventListener('animationend', animationEndHandler);
        };
      }
    }
  }, [progress, session, showJurMsg, gamblingCommissionTitle, gamblingCommissionText]);

  return curtainVisible;
};
